/**
 * Distance and length formatting utilities
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { DistanceFormat, FieldValue, FormattedValue } from "./types";

/**
 * Format as meters
 */
export const toMeters = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} m`;
};

/**
 * Format as centimeters
 */
export const toCentimeters = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value * 100).toFixed(decimals)} cm`;
};

/**
 * Format as millimeters
 */
export const toMillimeters = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return `${(value * 1000).toFixed(decimals)} mm`;
};

/**
 * Format as kilometers
 */
export const toKilometers = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} km`;
};

/**
 * Format as feet
 */
export const toFeet = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} ft`;
};

/**
 * Format as inches
 */
export const toInches = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} in`;
};

/**
 * Format as miles
 */
export const toMiles = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} mi`;
};

/**
 * Format as yards
 */
export const toYards = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} yd`;
};

/**
 * Format as nautical miles
 */
export const toNauticalMiles = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} nm`;
};

/**
 * Conversion utilities
 */
export const metersToFeet = (meters: number): number => meters * 3.28084;
export const feetToMeters = (feet: number): number => feet / 3.28084;
export const milesToKilometers = (miles: number): number => miles * 1.60934;
export const kilometersToMiles = (km: number): number => km / 1.60934;
export const feetToMiles = (feet: number): number => feet / 5280;
export const milesToFeet = (miles: number): number => miles * 5280;
export const metersToMiles = (meters: number): number => meters / 1609.34;
export const milesToMeters = (miles: number): number => miles * 1609.34;
export const yardsToMeters = (yards: number): number => yards * 0.9144;
export const metersToYards = (meters: number): number => meters / 0.9144;
export const inchesToCentimeters = (inches: number): number => inches * 2.54;
export const centimetersToInches = (cm: number): number => cm / 2.54;

/**
 * Auto-scale distance to the most appropriate unit
 */
export const autoScaleDistance = (
  value: number | null | undefined,
  metric: boolean = false,
  decimals: number = 1
): string | null => {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  if (metric) {
    // Assuming value is in meters
    if (absValue >= 1000) {
      return toKilometers(value, decimals);
    } else if (absValue < 0.01) {
      return toMillimeters(value, 0);
    } else if (absValue < 1) {
      return toCentimeters(value, decimals);
    } else {
      return toMeters(value, decimals);
    }
  } else {
    // Assuming value is in miles for imperial
    if (absValue < 0.01) {
      // Convert to feet
      return toFeet(milesToFeet(value), 0);
    } else if (absValue < 0.1) {
      // Convert to yards
      return toYards(milesToFeet(value) / 3, 0);
    } else {
      return toMiles(value, decimals);
    }
  }
};

/**
 * Format distance according to DistanceFormat specification
 */
export const formatDistance = (value: FieldValue, format: DistanceFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals ?? 1;

  if (format.autoScale) {
    const isMetric = format.unit === "m" || format.unit === "km";
    return autoScaleDistance(numValue, isMetric, decimals);
  }

  switch (format.unit) {
    case "km":
      return toKilometers(numValue, decimals);
    case "ft":
      return toFeet(numValue, decimals);
    case "mi":
      return toMiles(numValue, decimals);
    case "yd":
      return toYards(numValue, decimals);
    case "m":
    default:
      return toMeters(numValue, decimals);
  }
};

// Register distance formatters
FormatRegistry.register("miles", (value, decimals) => {
  if (typeof value === "number") {
    return toMiles(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

FormatRegistry.register("kilometers", (value, decimals) => {
  if (typeof value === "number") {
    return toKilometers(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

FormatRegistry.register("meters", (value, decimals) => {
  if (typeof value === "number") {
    return toMeters(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

FormatRegistry.register("feet", (value, decimals) => {
  if (typeof value === "number") {
    return toFeet(value, typeof decimals === "number" ? decimals : 0);
  }
  return null;
});

FormatRegistry.register("yards", (value, decimals) => {
  if (typeof value === "number") {
    return toYards(value, typeof decimals === "number" ? decimals : 0);
  }
  return null;
});

FormatRegistry.register("inches", (value, decimals) => {
  if (typeof value === "number") {
    return toInches(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

FormatRegistry.register("autoScaleDistance", (value, metric, decimals) => {
  if (typeof value === "number") {
    return autoScaleDistance(
      value,
      typeof metric === "boolean" ? metric : false,
      typeof decimals === "number" ? decimals : 1
    );
  }
  return null;
});
